/**
 * Spreader Settings Lookup engine — pure functions, no side effects.
 *
 * Resolves a (spreader model, product) pair into one of four honest answers.
 * There is deliberately NO 'estimated' branch: the site publishes a dial number
 * only where a label or a manufacturer chart supplies one, and answers every
 * other pairing with the target rate per 1,000 sq ft plus a calibration method.
 */

import {
  LABEL_DEFER_PRODUCT_IDS,
  LABEL_DEFER_REASON,
  findHandheldProhibition,
  findSettingRow,
  getSpreaderModel,
  getSpreaderProduct,
  type HandheldProhibition,
  type SettingScaleId,
  type SettingSource,
  type SpreaderModel,
  type SpreaderProduct,
} from '@/data/spreader-settings'

export type RateUnit = 'lb' | 'fl oz'

export type CalibrateReason =
  | 'unknown-scale'
  | 'handheld-scale'
  | 'unlisted-dial'
  | 'no-published-row'

interface LookupBase {
  spreader: SpreaderModel
  product: SpreaderProduct
}

interface RateCarrying {
  targetPer1000: number
  rateUnit: RateUnit
  /** Preformatted number only, e.g. "12.8". */
  targetAmountLabel: string
  /** Preformatted, e.g. "12.8 lb per 1,000 sq ft". */
  targetLabel: string
}

export type SpreaderLookup = LookupBase &
  (
    | ({
        kind: 'verified'
        setting: string
        source: SettingSource
        citation: string
        verifiedOn: string
        note?: string
      } & RateCarrying)
    | ({ kind: 'label-defer'; reason: string } & RateCarrying)
    | { kind: 'liquid'; reason: string }
    // Deliberately carries NO rate. A rate on this card reads as "here is how
    // much to put through the spreader the label just told you not to use."
    | { kind: 'prohibited'; prohibition: HandheldProhibition }
    | ({ kind: 'calibrate'; reason: CalibrateReason; reasonCopy: string } & RateCarrying)
  )

export interface SpreaderLookupInput {
  spreaderId: string
  productId: string
  areaSqFt?: number
}

const CALIBRATE_REASON_COPY: Record<CalibrateReason, string> = {
  'unknown-scale':
    "nobody publishes a cross-reference between the Scotts dial and this brand's gate. Gate opening, granule size, walking speed, and hopper shape all differ, so a Scotts number on another brand's dial is a guess dressed up as a fact.",
  'handheld-scale':
    'a handheld spreader meters on its own dial, and the numbers printed on a bag are rotary and drop numbers, not handheld ones. Nobody publishes a cross-reference between them, and your crank speed moves the rate as much as the dial does.',
  'unlisted-dial':
    'a bag prints dial numbers only for specific branded spreaders, and your spreader is not one of them. The published numbers below are Scotts numbers for Scotts dials, so they do not carry across.',
  'no-published-row':
    'no label or manufacturer chart we can cite pairs this bag with your dial. Manufacturers publish settings only for the combinations they test.',
}

/** Scales that belong to a named manufacturer's dial rather than "any brand". */
const BRANDED_SCALES = new Set<SettingScaleId>(['scotts-rotary', 'scotts-drop'])

function clampNonNegative(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0
  return value
}

function roundTo(value: number, places: number): number {
  const f = 10 ** places
  return Math.round(value * f) / f
}

function formatAmount(value: number): string {
  if (value === 0) return '0'
  if (value >= 100) return String(Math.round(value))
  return String(roundTo(value, value < 10 ? 2 : 1))
}

function rateFields(product: SpreaderProduct): RateCarrying {
  const amount = formatAmount(product.ratePer1000)
  return {
    targetPer1000: product.ratePer1000,
    rateUnit: product.rateUnit,
    targetAmountLabel: amount,
    targetLabel: `${amount} ${product.rateUnit} per 1,000 sq ft`,
  }
}

/**
 * Resolution order, most specific first:
 *   liquid product        -> 'liquid'        (no hopper involved at all)
 *   handheld + label ban  -> 'prohibited'    (the label rules the pair out)
 *   model scale 'unknown' -> 'calibrate'     (no sourced cross-reference)
 *   label defers          -> 'label-defer'   (a number exists but varies by bag)
 *   published row         -> 'verified'
 *   otherwise             -> 'calibrate'
 * The unknown scale outranks the label defer on purpose: the dial number a
 * label-defer bag prints is a Scotts number, so on a brand with no published
 * cross-reference it is unusable and calibration is the only honest answer.
 * Every branch except 'liquid' carries the target rate, so the user always
 * leaves with a number they can act on.
 */
export function lookupSpreaderSetting(input: SpreaderLookupInput): SpreaderLookup | null {
  const spreader = getSpreaderModel(input.spreaderId)
  const product = getSpreaderProduct(input.productId)
  if (!spreader || !product) return null

  const base = { spreader, product }

  if (product.form === 'liquid') {
    return {
      ...base,
      kind: 'liquid',
      reason:
        'This one is a liquid. It goes through a hose-end sprayer or a pump sprayer, so there is no hopper and no dial setting to look up.',
    }
  }

  // Outranks every calibration branch: a calibration walk answers "what number
  // gets this bag onto the lawn", which is the wrong question once the label
  // has ruled the equipment out entirely.
  if (spreader.type === 'handheld') {
    const prohibition = findHandheldProhibition(product.id)
    if (prohibition) return { ...base, kind: 'prohibited', prohibition }
  }

  if (spreader.scaleId === 'unknown') {
    // A handheld with no sourced scale is a different story from a push
    // spreader with no sourced scale, so it gets its own copy rather than a
    // line about another brand's gate.
    const reason: CalibrateReason = spreader.type === 'handheld' ? 'handheld-scale' : 'unknown-scale'
    return {
      ...base,
      kind: 'calibrate',
      reason,
      reasonCopy: CALIBRATE_REASON_COPY[reason],
      ...rateFields(product),
    }
  }

  if (LABEL_DEFER_PRODUCT_IDS.includes(product.id)) {
    return { ...base, kind: 'label-defer', reason: LABEL_DEFER_REASON, ...rateFields(product) }
  }

  const row = findSettingRow(product.id, spreader.scaleId)
  if (row) {
    return {
      ...base,
      kind: 'verified',
      setting: row.setting,
      source: row.source,
      citation: row.citation,
      verifiedOn: row.verifiedOn,
      note: row.note,
      ...rateFields(product),
    }
  }

  // A branded dial with no sourced row is a gap in what we can cite. An
  // "any brand" dial is a different claim: the numbers below exist, they are
  // just printed for dials this spreader does not have.
  const reason: CalibrateReason = BRANDED_SCALES.has(spreader.scaleId)
    ? 'no-published-row'
    : 'unlisted-dial'

  return {
    ...base,
    kind: 'calibrate',
    reason,
    reasonCopy: CALIBRATE_REASON_COPY[reason],
    ...rateFields(product),
  }
}

export interface TotalForArea {
  total: number
  totalLabel: string
  areaSqFt: number
}

/** Total product for a lawn, at the label rate. Non-finite input clamps to 0. */
export function totalProductForArea(ratePer1000: number, areaSqFt: number | undefined): TotalForArea {
  const rate = clampNonNegative(ratePer1000)
  const area = clampNonNegative(areaSqFt)
  const total = roundTo((rate * area) / 1000, 2)
  return { total, totalLabel: formatAmount(total), areaSqFt: area }
}

/**
 * Half rate, for the two-perpendicular-passes calibration walk. Splitting the
 * load and crossing the passes is what hides the striping that shows up as
 * green stripes three weeks later.
 */
export function halfRateCrossPattern(ratePer1000: number): { half: number; halfLabel: string } {
  const half = roundTo(clampNonNegative(ratePer1000) / 2, 2)
  return { half, halfLabel: formatAmount(half) }
}

/**
 * Product to weigh out for a 1,000 sq ft calibration strip. That is the rate
 * itself, exposed as its own helper so the UI and the HowTo can't drift.
 */
export function testStripAmount(ratePer1000: number): { amount: number; amountLabel: string } {
  const amount = roundTo(clampNonNegative(ratePer1000), 2)
  return { amount, amountLabel: formatAmount(amount) }
}
